import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ThumbsDown, ThumbsUp, X } from 'lucide-react';

const MAX_FEEDBACK_LENGTH = 500;

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number, feedback: string) => void;
  isLoading: boolean;
  title: string;
  subtitle: string;
  placeholder: string;
  selectedMetricName?: string; // Optional: Show selected metric name
  /** Pre-populate the rating when displaying existing feedback (5 = 👍, 1 = 👎, 0 = none). */
  initialRating?: number;
  /** Pre-populate the text when displaying existing feedback. */
  initialFeedback?: string;
  /** When true, render previously submitted feedback in read-only mode. */
  readOnly?: boolean;
  /** ISO-8601 timestamp shown below the submitted comment in read-only mode. */
  submittedAt?: string | null;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  title,
  subtitle,
  placeholder,
  selectedMetricName,
  initialRating = 0,
  initialFeedback = '',
  readOnly = false,
  submittedAt = null,
}) => {
  const [rating, setRating] = useState(initialRating);
  const [feedback, setFeedback] = useState(initialFeedback);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const submittedRef = useRef(false);

  const trimmedFeedback = feedback.trim();
  const isRatingValid = rating === 5 || rating === 1;
  const isCommentValid = trimmedFeedback.length > 0;
  const showCommentError = !readOnly && hasTriedSubmit && !isCommentValid;
  const canSubmit = isRatingValid && isCommentValid && !isLoading;

  const handleSubmit = () => {
    if (readOnly) return;
    setHasTriedSubmit(true);
    if (!isRatingValid) return;
    if (!isCommentValid) return;
    if (submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(rating, trimmedFeedback);
  };

  const inputsDisabled = isLoading || readOnly;
  const feedbackLength = feedback.length;

  const submittedAtLabel = (() => {
    if (!readOnly || !submittedAt) return null;
    const parsed = new Date(submittedAt);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  })();

  const handleFeedbackChange = (value: string) => {
    setFeedback(value.slice(0, MAX_FEEDBACK_LENGTH));
  };

  const clearDraftFeedback = () => {
    if (readOnly) return;
    setRating(0);
    setFeedback('');
    setHoveredRating(0);
    setHasTriedSubmit(false);
    submittedRef.current = false;
  };

  const handleClose = () => {
    clearDraftFeedback();
    onClose();
  };

  // Re-seed local state from the supplied initials whenever the modal opens
  // (the same modal instance is re-used for different target messages, so we
  // must refresh on every open). When the modal closes we leave local state
  // alone — the next open will overwrite from the next target's initials.
  useEffect(() => {
    if (isOpen) {
      setRating(initialRating);
      setFeedback(initialFeedback);
      setHoveredRating(0);
      setHasTriedSubmit(false);
      submittedRef.current = false;
    }
  }, [isOpen, initialRating, initialFeedback]);

  useEffect(() => {
    if (!isLoading) submittedRef.current = false;
  }, [isLoading]);

  if (!isOpen) return null;

  return createPortal(
    <div
      key={`feedback-modal-${title}`} // Ensure proper re-rendering for different contexts
      className="fixed inset-0 flex items-center justify-center bg-[#1F1A20]/45 p-4"
      style={{
        zIndex: 99999, // Extremely high z-index to ensure it appears above everything
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        isolation: 'isolate'
      }}
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-0 shadow-xl"
        style={{ zIndex: 100000 }} // Even higher for the modal content
        onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking inside modal
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-white/70 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Close feedback modal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex flex-col">
          <div className="rounded-t-lg border-b border-gray-200 px-5 py-2 pr-12">
            <h3 className="pt-1 text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-xs leading-5 text-gray-600">
              {readOnly && submittedAtLabel ? `Submitted ${submittedAtLabel}` : subtitle}
            </p>
            {selectedMetricName && (
              <div className="mt-2 inline-block rounded-full bg-primary/10 px-3 py-1.5 text-primary">
                <span className="text-sm font-medium">{selectedMetricName}</span>
              </div>
            )}
          </div>

          <div className="space-y-3 px-5 pt-3.5">
            <p className="text-sm font-semibold text-gray-900">
              How was this response?
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => !readOnly && setRating(5)}
                onMouseEnter={() => !readOnly && setHoveredRating(5)}
                onMouseLeave={() => !readOnly && setHoveredRating(0)}
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[1.25rem] border transition-colors ${
                  readOnly && rating !== 5
                    ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                    : rating === 5
                      ? 'border-[#3F7D4F] bg-[#ECF3EE] text-[#2E5F3B]'
                      : hoveredRating === 5
                        ? 'border-[#3F7D4F]/60 bg-[#ECF3EE] text-[#2E5F3B]'
                        : 'border-[#3F7D4F] bg-white text-[#3F7D4F] hover:bg-[#ECF3EE]'
                } ${readOnly && rating === 5 ? 'cursor-default' : ''}`}
                disabled={inputsDisabled}
                aria-pressed={rating === 5}
                aria-label="Positive feedback"
                title={readOnly ? 'Submitted positive feedback' : 'Positive'}
              >
                <ThumbsUp className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => !readOnly && setRating(1)}
                onMouseEnter={() => !readOnly && setHoveredRating(1)}
                onMouseLeave={() => !readOnly && setHoveredRating(0)}
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[1.25rem] border transition-colors ${
                  readOnly && rating !== 1
                    ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                    : rating === 1
                      ? 'border-[#B23A48] bg-[#FBE7E7] text-[#8E2A36]'
                      : hoveredRating === 1
                        ? 'border-[#B23A48]/60 bg-[#FBE7E7] text-[#8E2A36]'
                        : 'border-[#B23A48] bg-white text-[#B23A48] hover:bg-[#FBE7E7]'
                } ${readOnly && rating === 1 ? 'cursor-default' : ''}`}
                disabled={inputsDisabled}
                aria-pressed={rating === 1}
                aria-label="Negative feedback"
                title={readOnly ? 'Submitted negative feedback' : 'Negative'}
              >
                <ThumbsDown className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-2 px-5 pb-3">
            <div className="relative">
              <textarea
                value={feedback}
                onChange={(e) => {
                  handleFeedbackChange(e.target.value);
                  if (hasTriedSubmit) setHasTriedSubmit(false);
                }}
                placeholder={readOnly ? 'No comment was submitted.' : placeholder}
                className={`min-h-[68px] w-full rounded-md border p-4 pb-8 text-sm leading-5 placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-100 ${
                  inputsDisabled
                    ? 'resize-none border-gray-300 bg-gray-50 text-gray-600 cursor-default'
                    : showCommentError
                      ? 'resize-y border-[#B23A48] bg-white text-gray-900 focus:border-[#B23A48] focus:ring-[#B23A48]/20'
                      : 'resize-y border-gray-300 bg-white text-gray-900'
                }`}
                rows={2}
                disabled={inputsDisabled}
                readOnly={readOnly}
                maxLength={MAX_FEEDBACK_LENGTH}
                required={!readOnly}
                aria-required={!readOnly}
                aria-invalid={showCommentError}
                aria-describedby={showCommentError ? 'feedback-comment-error' : undefined}
                aria-label={readOnly ? 'Submitted feedback comment' : 'Add a required comment'}
              />
              {!readOnly && (
                <span className="pointer-events-none absolute bottom-3 right-3 text-xs text-gray-500">
                  {feedbackLength}/{MAX_FEEDBACK_LENGTH}
                </span>
              )}
            </div>
            {showCommentError && (
              <p id="feedback-comment-error" className="text-xs font-medium text-[#B23A48]">
                Comment is required.
              </p>
            )}
          </div>

          {!readOnly && (
            <div className="flex justify-end px-5 py-4 pt-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-1 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/50"
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
